import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

// Admin config: map each item category (item_master.category4) to a Halnode ERP
// stock point. Used by "Convert to DC in ERP" to pick stock_point_id per item.
const ErpStockPointsDialog = ({ open, onClose, isAdmin }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [tokenOk, setTokenOk] = useState(false);
  const [meta, setMeta] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/wms/fg/erp/stock-points');
      setRows((res.data.mappings || []).map((m) => ({ ...m })));
      setCats(res.data.available_categories || []);
      setTokenOk(!!res.data.token_configured);
      setMeta({ is_seed: res.data.is_seed, updated_by: res.data.updated_by, updated_at: res.data.updated_at });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ERP stock points');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const setRow = (i, key, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  const addRow = () => setRows((r) => [...r, { category: '', stock_point_id: '' }]);
  const delRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const save = async () => {
    const clean = rows.filter((r) => (r.category || '').trim() && (r.stock_point_id || '').trim());
    setSaving(true);
    try {
      await apiClient.put('/wms/fg/erp/stock-points', { mappings: clean });
      toast.success('ERP stock points saved');
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl" data-testid="erp-stockpoints-dialog">
        <DialogHeader>
          <DialogTitle>ERP Stock Points</DialogTitle>
          <DialogDescription>
            Map each item category to a Halnode ERP stock point. "Convert to DC in ERP" sends one call per stock point.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 text-sm">
          <span>ERP token:</span>
          <Badge variant="outline" className={tokenOk ? 'border-emerald-300 text-emerald-700' : 'border-rose-300 text-rose-700'} data-testid="erp-token-status">
            {tokenOk ? 'Configured' : 'Not configured'}
          </Badge>
          {meta?.is_seed && <span className="text-amber-600 text-xs">(showing default seed — save to persist)</span>}
        </div>

        {loading ? (
          <div className="py-8 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-medium text-slate-500 px-1">
              <span>Category (item_master.category4)</span>
              <span>Stock Point ID (sent to ERP)</span>
              <span></span>
            </div>
            {rows.map((row, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`erp-sp-row-${i}`}>
                <Input list="erp-cat-list" value={row.category} disabled={!isAdmin}
                  onChange={(e) => setRow(i, 'category', e.target.value)}
                  placeholder="e.g. Dhoti" data-testid={`erp-sp-category-${i}`} />
                <Input value={row.stock_point_id} disabled={!isAdmin}
                  onChange={(e) => setRow(i, 'stock_point_id', e.target.value)}
                  placeholder="e.g. 12 or NEW FG WH" data-testid={`erp-sp-id-${i}`} />
                <Button variant="ghost" size="sm" disabled={!isAdmin} onClick={() => delRow(i)}
                  className="text-rose-600" data-testid={`erp-sp-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <datalist id="erp-cat-list">
              {cats.map((c) => <option key={c} value={c} />)}
            </datalist>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={addRow} className="mt-1" data-testid="erp-sp-add">
                <Plus className="w-4 h-4 mr-1" /> Add mapping
              </Button>
            )}
            {!isAdmin && <p className="text-xs text-slate-400">Read-only — only Admins can edit these mappings.</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="erp-sp-cancel">Cancel</Button>
          {isAdmin && (
            <Button onClick={save} disabled={saving || loading} data-testid="erp-sp-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ErpStockPointsDialog;
